import Stopwatch from '@app/components/stopwatch';
import { SvgIcon } from '@common/sprites';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import classes from './timer.module.scss';

type Phase = 'free' | 'paid';
type State = 'initial' | 'started' | 'finishing';

type Props = {
  freeDuration: number,
  paidDuration: number,
  state: State,
  buyerDurationCounterDirection: 'stopwatch' | 'timer',
  events?: {
    onStarted?: () => void,
    onEnded?: () => void,
    onDurationUpdated?: (time: number) => void
  },
  includeHours?: boolean,
  showIndicator?: boolean,
  callType: 'chat' | 'voip_chat',
};

const formatTime = (value: number, includeHours: boolean): string => {
  const hours = includeHours ? Math.floor(value / 3600).toString().padStart(2, '0') : '';
  const minutes = Math.floor(includeHours ? (value % 3600) / 60 : value / 60).toString().padStart(2, '0');
  const seconds = (value % 60).toString().padStart(2, '0');
  return `${ includeHours ? `${ hours }:` : '' }${ minutes }:${ seconds }`;
};

const Timer: React.FC<Props> = ({
  freeDuration,
  paidDuration,
  state,
  buyerDurationCounterDirection,
  events,
  callType,
  includeHours = false,
  showIndicator = false
}) => {
  const totalDuration = freeDuration + paidDuration;

  const { t } = useTranslation();
  const [displayTime, setDisplayTime] = useState(totalDuration);
  const [phase, setPhase] = useState<Phase>('free');
  const startTimeRef = useRef<number>(Date.now());
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const timerUpdateRef = useRef<NodeJS.Timeout | null>(null);

  const getCurrentTime = () => {
    const now = Date.now();
    const elapsed = Math.floor((now - startTimeRef.current) / 1000);
    return totalDuration - elapsed;
  };

  const processTick = () => {
    const currentTime = getCurrentTime();

    setPhase(prev => {
      if (currentTime <= paidDuration && prev !== 'paid') {
        events?.onStarted?.();
        return 'paid';
      }

      return prev;
    });

    setDisplayTime(Math.max(currentTime, 0));
  };

  const cleanup = () => {
    setDisplayTime(0);
    clearInterval(timerRef.current!);
    clearInterval(timerUpdateRef.current!);
  };

  const processUpdateAndFinish = () => {
    const currentTime = getCurrentTime();
    events?.onDurationUpdated?.(currentTime);
    if (currentTime <= 0) {
      cleanup();
      events?.onEnded?.();
    }
  };

  const renderValue = () => {
    if (state === 'finishing') return t('chat.end_call_time_label');
    if (phase === 'free') return t('chat.free_setup_time');
    return formatTime(displayTime, includeHours);
  };

  useEffect(() => {
    if (state === 'started') {
      startTimeRef.current = Date.now();
      timerRef.current = setInterval(processTick, 1000);
      timerUpdateRef.current = setInterval(processUpdateAndFinish, 500);
    }

    return () => cleanup();
  }, [state, paidDuration, freeDuration, buyerDurationCounterDirection]);

  return buyerDurationCounterDirection === 'stopwatch' ? (
    <Stopwatch
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore next-line
      secondsCount={ displayTime }
      paidDuration={ paidDuration }
      callType={ callType }
    />
  ) : (
    <div className={ classes.container }>
      {showIndicator && (
        <SvgIcon
          className={ classes.indicator }
          size={ 13 }
          id="timer_indicator"
        />
      )}
      <span className={ classes.value }>
        {renderValue()}
      </span>
    </div>
  );
};

export default Timer;
